import { Action, ActivityTranslationCode, Area } from '../../../models/activity-log.model';
import { Pipe, PipeTransform } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { FeatureBadgeStatus } from 'app/components/feature-badge/feature-badge.component';

type LinkRoute = { area: Area, name: "staff" | "camera-configuration" | "route" | "device" | "teams" };

export function escapeHtml(value: string): string {
  if (value == null || value === '') return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
}

/**
 * HTML-escapes a plain text value. Use this for every user-controlled value (names, IDs) that is passed as a
 * translation parameter into a string that is rendered with [innerHTML].
 */
@Pipe({
  name: "escapeHtml",
  standalone: true,
  pure: true,
})
export class EscapeHtmlPipe implements PipeTransform {
  transform(value: string | null | undefined): string {
    return escapeHtml(value as string);
  }
}

/**
 * HTML-escapes a plain text value and highlights the first match of the search text.
 * Safe to render with [innerHTML]; special characters in the value are shown as is.
 */
@Pipe({
  name: "escapeHighlight",
  standalone: true,
  pure: true,
})
export class EscapeHighlightPipe implements PipeTransform {
  /**
   * @param value - Plain text value
   * @param searchText - Text to highlight
   * @param asLink - Wrap the match in a link (used inside links) instead of a span
   */
  transform(value: string | null | undefined, searchText: string = "", asLink: boolean = false): string {
    if (value == null) return "";
    const text = String(value);
    const start = searchText ? text.toLocaleLowerCase().indexOf(searchText.toLocaleLowerCase()) : -1;
    if (start === -1) return escapeHtml(text);

    const end = start + searchText.length;
    const match = escapeHtml(text.slice(start, end));
    const wrapped = asLink ? `<a href="#" class='yellow'>${match}</a>` : `<span class='yellow'>${match}</span>`;
    return `${escapeHtml(text.slice(0, start))}${wrapped}${escapeHtml(text.slice(end))}`;
  }
}

const linkRoutes: LinkRoute[] = [
  { area: "Staff", name: "staff" },
  { area: "Digital Supervision", name: "camera-configuration" },
  { area: 'AlarmRoute', name: 'route' },
  { area: 'DigitalKeys', name: 'device' },
  { area: 'AlarmUserGroup', name: 'teams' },
  { area: 'Location', name: 'device' },
  { area: 'EMA', name: 'device' },
  { area: 'Devices', name: 'device' },
];

/**
 * This pipe takes a translated string and wraps targetName and targetId into an anchor tag.
 * The translated string must already be HTML-safe, i.e. its parameters escaped with the escapeHtml pipe.
 * targetName and targetId are given as plain text.
 */
@Pipe({
  name: "anchorWrap",
  standalone: true,
  pure: true,
})
export class AnchorWrapPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) { }

  /**
   * Transforms the input string by wrapping targetName and targetId into an anchor tag.
   * @param value - The input string to transform.
   * @param targetName - The name to be wrapped in the anchor tag.
   * @param targetId - The id to be wrapped in the anchor tag.
   * @param area - The area of the target.
   * @param searchText - The text to highlight in the anchor tag.
   * @param hideTargetId - Whether to hide the targetId in the anchor tag.
   * @returns - The transformed string with targetName and targetId wrapped in an anchor tag.
   */
  transform(value: string, targetName: string, targetId: string, area: Area, searchText: string = "", hideTargetId: boolean = false): SafeHtml {
    let result = "";

    if (!value || !targetName || !targetId || !area) {
      return value;
    }

    if (searchText) {
      result = this.wrapInHighlightedAnchorTag(value, targetName, targetId, area, searchText, hideTargetId);
    }
    else {
      result = this.wrapInAnchorTag(value, targetName, targetId, area, hideTargetId);
    }
    return this.sanitizer.bypassSecurityTrustHtml(result);
  }

  private resolveTargetString(targetName: string, targetId: string, area: Area, hideTargetId: boolean): string {
    // Camera groups use a GUID and phones an EMA mobile ID as targetId, so we don't want to show it in the anchor tag
    if (!hideTargetId && area !== "Digital Supervision" && area !== "MobileWhitelist" && targetId) {
      return `${targetName} (${targetId})`;
    }

    return targetName;
  }

  private resolveRoute(area: Area, targetId: string): string {
    if (area === "Location") {
      return ""; 
    }
    // Role events target a role (numeric ID), assignments and individual permissions a staff member
    if (area === "AccessRole") {
      return /^\d+$/.test(targetId) ? `/roles/${targetId}` : `/staff/${targetId}`;
    }
    // Phones have no page of their own, link to the whitelist
    if (area === "MobileWhitelist") {
      return "/mobile-whitelist";
    }
    const route = linkRoutes.find((r) => r.area === area);
    return route ? `/${route.name}/${targetId}` : "/dashboard";
  }

  private wrapInAnchorTag(value: string, targetName: string, targetId: string, area: Area, hideTargetId): string {
    // The translated value contains the escaped target string, so search for and insert the escaped form
    const targetString = escapeHtml(this.resolveTargetString(targetName, targetId, area, hideTargetId));
    const start = value.indexOf(targetString);
    const end = start + targetString.length;

    if (start === -1) {
      return value;
    }
  
    if (area === "Location") {
      return `
        ${value.slice(0, start)}
        <a class="location-link" data-location-id="${escapeHtml(targetId)}" style="cursor: pointer; text-decoration: underline;">
          ${targetString}
        </a>
        ${value.slice(end)}
      `;
    }
  
    return `
      ${value.slice(0, start)}
        <a class="link ${escapeHtml(area.toLocaleLowerCase())}"
          data-url="${escapeHtml(this.resolveRoute(area, targetId))}"
          style="cursor: pointer; text-decoration: underline;">
          ${targetString}
        </a>
      ${value.slice(end)}
    `;
  }

  private wrapInHighlightedAnchorTag(value: string, targetName: string, targetId: string, area: Area, searchText: string, hideTargetId: boolean): string {
    const targetString = this.resolveTargetString(targetName, targetId, area, hideTargetId).replace(/&#x2011;/g, '-');

    // The translated value contains the escaped target string
    const escapedTargetString = escapeHtml(targetString);
    const start = value.toLocaleLowerCase().indexOf(escapedTargetString.toLocaleLowerCase());
    const end = start + escapedTargetString.length;

    if (start === -1) {
      return value;
    }

    const searchStart = targetString.toLocaleLowerCase().indexOf(searchText.toLocaleLowerCase());
    const searchEnd = searchStart + searchText.length;

    let highlightedTargetString = "";

    if (searchStart !== -1) {
      const before = escapeHtml(targetString.slice(0, searchStart));
      const match = escapeHtml(targetString.slice(searchStart, searchEnd));
      const after = escapeHtml(targetString.slice(searchEnd));
      highlightedTargetString = `${before}<span class="yellow" onclick="event.target.parentNode.click();">${match}</span>${after}`;
    } else {
      highlightedTargetString = escapeHtml(targetString);
    }

    return `${value.slice(0, start)}<a class="link" data-url="${escapeHtml(this.resolveRoute(area, targetId))}" style="cursor: pointer; text-decoration: underline;">${highlightedTargetString}</a>${value.slice(end)}`;
  }
}

/**
 * This pipe takes a translated string and wraps targetName and targetId into an strong tag.
 */
@Pipe({
  name: "strongWrap",
  standalone: true,
  pure: true,
})
export class StrongWrapPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) { }

  transform(value: string, targetString: string): SafeHtml {
    // The translated value must already be HTML-safe and contain the escaped target string
    let result = value;
    const escapedTargetString = escapeHtml(targetString);
    const start = escapedTargetString ? result.indexOf(escapedTargetString) : -1;
    const end = start + escapedTargetString.length;

    if (start !== -1) {
      result = `
        ${result.slice(0, start)}
          <strong>${escapedTargetString}</strong>
        ${result.slice(end)}`;
    }
    return this.sanitizer.bypassSecurityTrustHtml(result);
  }
}

/**
 * Pipe to parse target class to a class name.
 */
@Pipe({
  name: "parseTargetClass",
  standalone: true,
  pure: true,
})
export class ParseTargetClassPipe implements PipeTransform {

  transform(value: string, action: Action): string {
    if (!value || !action) {
      return "";
    }

    value += ` ${action}`;

    const targetClass = value
      .toLowerCase()
      .trim()
      .replace(/ /g, "-");

    return targetClass;
  }
}

@Pipe({
  name: "resolveActivityBadgeStatus",
  standalone: true,
  pure: true,
})
export class ResolveActivityBadgeStatusPipe implements PipeTransform {
  transform(value: ActivityTranslationCode): FeatureBadgeStatus {
    let status: FeatureBadgeStatus;

    switch (value) {
      case "USER_LOGIN_FAILED":
        status = "warning";
        break;
      case "TEMPORARY_CODE_GENERATED":
        status = "warning";
        break;
      default:
        status = "default";
        break;
    }

    return status;
  }
}
